import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout';
import { enrollmentsAPI, coursesAPI, cohortsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import {
  FolderOpen,
  ExternalLink,
  Loader2,
  AlertCircle,
  Upload,
  BookOpen,
  GraduationCap,
  ChevronRight,
} from 'lucide-react';

// Helper to extract Google Drive folder ID from various URL formats
const extractDriveFolderId = (input) => {
  if (!input) return null;
  
  if (/^[a-zA-Z0-9_-]+$/.test(input) && input.length > 20) {
    return input;
  }
  
  const patterns = [
    /\/folders\/([a-zA-Z0-9_-]+)/,
    /id=([a-zA-Z0-9_-]+)/,
    /\/d\/([a-zA-Z0-9_-]+)/,
    /^([a-zA-Z0-9_-]{25,})/,
  ];
  
  for (const pattern of patterns) {
    const match = input.match(pattern);
    if (match) return match[1];
  }
  
  return input;
};

const getEmbedUrl = (folderId) => {
  if (!folderId) return null;
  const id = extractDriveFolderId(folderId);
  // Use embeddedfolderview for viewing, or folder view for upload capability
  return `https://drive.google.com/embeddedfolderview?id=${id}#list`;
};

const getDirectLink = (folderId) => {
  if (!folderId) return null;
  const id = extractDriveFolderId(folderId);
  return `https://drive.google.com/drive/folders/${id}`;
};

export const MyCourseworkPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState({});
  const [cohorts, setCohorts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get user's enrollments
        const enrollmentsRes = await enrollmentsAPI.getMyEnrollments();
        const enrollmentsWithFolders = enrollmentsRes.data.filter(e => e.personal_drive_folder);
        setEnrollments(enrollmentsRes.data);
        
        // If only one enrollment has a folder, auto-select it
        if (enrollmentsWithFolders.length === 1) {
          setSelectedEnrollment(enrollmentsWithFolders[0]);
        }
        
        // Fetch course and cohort details
        const coursesRes = await coursesAPI.getAll();
        const cohortsRes = await cohortsAPI.getAll();
        
        const coursesMap = {};
        coursesRes.data.forEach(c => { coursesMap[c.course_id] = c; });
        setCourses(coursesMap);
        
        const cohortsMap = {};
        cohortsRes.data.forEach(c => { cohortsMap[c.cohort_id] = c; });
        setCohorts(cohortsMap);
        
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const enrollmentsWithFolders = enrollments.filter(e => e.personal_drive_folder);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#41EAD4]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800">My Coursework</h1>
          <p className="text-slate-500">Upload and manage your assignment submissions</p>
        </div>

        {enrollmentsWithFolders.length === 0 ? (
          /* No Folders Assigned */
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FolderOpen className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Coursework Folder Assigned</h3>
              <p className="text-slate-500 text-center max-w-md">
                Your instructor hasn't set up a personal coursework folder yet. 
                Please contact your course administrator.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Course/Enrollment Selector - Left Sidebar */}
            <div className="lg:col-span-1 space-y-3">
              <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-4">
                Your Courses
              </h2>
              {enrollmentsWithFolders.map((enrollment) => {
                const course = courses[enrollment.course_id];
                const cohort = cohorts[enrollment.cohort_id];
                const isSelected = selectedEnrollment?.enrollment_id === enrollment.enrollment_id;
                
                return (
                  <Card
                    key={enrollment.enrollment_id}
                    className={`cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-[#41EAD4] bg-[#41EAD4]/5 shadow-md' 
                        : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                    }`}
                    onClick={() => setSelectedEnrollment(enrollment)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-[#41EAD4]' : 'bg-slate-100'
                        }`}>
                          <GraduationCap className={`w-5 h-5 ${isSelected ? 'text-[#0D1B2A]' : 'text-slate-500'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-medium truncate ${isSelected ? 'text-[#0D1B2A]' : 'text-slate-700'}`}>
                            {course?.title || 'Course'}
                          </h3>
                          <p className="text-xs text-slate-500 truncate">
                            {cohort?.name || 'Cohort'}
                          </p>
                        </div>
                        {isSelected && (
                          <ChevronRight className="w-5 h-5 text-[#41EAD4] flex-shrink-0" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Main Content - Drive Embed */}
            <div className="lg:col-span-3">
              {!selectedEnrollment ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-16">
                    <BookOpen className="w-12 h-12 text-slate-300 mb-4" />
                    <h3 className="text-lg font-medium text-slate-700 mb-2">Select a Course</h3>
                    <p className="text-slate-500 text-center">
                      Choose a course from the left to view your coursework folder
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-[#0D1B2A] to-[#1B3A4B] text-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/10 rounded-lg">
                          <Upload className="w-5 h-5" />
                        </div>
                        <div>
                          <CardTitle className="text-white">
                            {courses[selectedEnrollment.course_id]?.title || 'Coursework'}
                          </CardTitle>
                          <CardDescription className="text-slate-300">
                            Upload your assignments and coursework here
                          </CardDescription>
                        </div>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => window.open(getDirectLink(selectedEnrollment.personal_drive_folder), '_blank')}
                        className="gap-2"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Open in Drive
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="relative bg-slate-50">
                      {/* Loading state */}
                      <div className="absolute inset-0 flex items-center justify-center bg-slate-50 z-0">
                        <div className="text-center">
                          <Loader2 className="w-8 h-8 animate-spin text-[#41EAD4] mx-auto mb-2" />
                          <p className="text-sm text-slate-500">Loading your folder...</p>
                        </div>
                      </div>
                      
                      {/* Google Drive Embed */}
                      <iframe
                        src={getEmbedUrl(selectedEnrollment.personal_drive_folder)}
                        title="My Coursework Folder"
                        className="w-full h-[550px] border-0 relative z-10"
                        style={{ minHeight: '550px' }}
                        sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Help Card */}
              {selectedEnrollment && (
                <Card className="mt-4 bg-amber-50 border-amber-100">
                  <CardContent className="py-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-amber-800">How to Upload</p>
                        <p className="text-sm text-amber-700 mt-1">
                          To upload files, click <strong>"Open in Drive"</strong> button above. 
                          In Google Drive, right-click and select "File upload" or drag and drop your files.
                          Your instructor will be able to see all uploaded files.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MyCourseworkPage;
